import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { WorkflowPhase, PHASE_CONFIG } from '../../shared/types/due-diligence';
import { Ionicons } from '@expo/vector-icons';

export interface PhaseIndicatorProps {
  currentPhase: WorkflowPhase;
  onPhaseClick?: (phase: WorkflowPhase) => void;
  showDescriptions?: boolean;
}

const PHASES: WorkflowPhase[] = [
  'setup',
  'initial_screen',
  'deep_diligence',
  'final_analysis',
  'decision',
];

export function PhaseIndicator({ 
  currentPhase, 
  onPhaseClick,
  showDescriptions = false
}: PhaseIndicatorProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const { isMobile, isSmallMobile } = useResponsive();
  const indicatorStyles = makeIndicatorStyles(tokens, utils, isMobile, isSmallMobile);

  // Safe fallback if currentPhase is undefined
  const safePhase = currentPhase && PHASE_CONFIG[currentPhase] ? currentPhase : 'setup';
  const currentPhaseOrder = PHASE_CONFIG[safePhase]?.order || 1;

  return (
    <View style={indicatorStyles.container}>
      {PHASES.map((phase, index) => {
        const config = PHASE_CONFIG[phase];
        if (!config) return null;
        const phaseOrder = config.order;
        const isActive = phase === safePhase;
        const isCompleted = phaseOrder < currentPhaseOrder;
        const isUpcoming = phaseOrder > currentPhaseOrder;
        const isClickable = !!onPhaseClick && (isCompleted || isActive);

        return (
          <React.Fragment key={phase}>
            <View
              style={[
                indicatorStyles.phaseContainer,
                isClickable && indicatorStyles.phaseClickable,
              ]}
              onTouchEnd={() => isClickable && onPhaseClick?.(phase)}
            >
              <View style={[
                indicatorStyles.phaseCircle,
                isActive && indicatorStyles.phaseCircleActive,
                isCompleted && indicatorStyles.phaseCircleCompleted,
                isUpcoming && indicatorStyles.phaseCircleUpcoming,
              ]}>
                {isCompleted ? (
                  <Ionicons
                    name="checkmark"
                    size={isMobile ? 16 : 18}
                    color={tokens.color.onPrimary}
                  />
                ) : (
                  <Text style={[
                    indicatorStyles.phaseNumber,
                    isActive && indicatorStyles.phaseNumberActive,
                  ]}>
                    {phaseOrder}
                  </Text>
                )}
              </View>
              <View style={indicatorStyles.phaseContent}>
                <Text style={[
                  indicatorStyles.phaseLabel,
                  isActive && indicatorStyles.phaseLabelActive,
                  isCompleted && indicatorStyles.phaseLabelCompleted,
                  isUpcoming && indicatorStyles.phaseLabelUpcoming,
                ]}>
                  {config.label}
                </Text>
                {showDescriptions && !isSmallMobile && (
                  <Text style={indicatorStyles.phaseDescription}>
                    {config.description}
                  </Text>
                )}
              </View>
            </View>
            {index < PHASES.length - 1 && (
              <View style={[
                indicatorStyles.connector,
                isCompleted && indicatorStyles.connectorCompleted,
                isUpcoming && indicatorStyles.connectorUpcoming,
              ]} />
            )}
          </React.Fragment>
        );
      })}
    </View>
  );
}

function makeIndicatorStyles(
  tokens: ReturnType<typeof useStyles>['tokens'],
  utils: ReturnType<typeof useStyles>['utils'],
  isMobile: boolean,
  isSmallMobile: boolean
) {
  return StyleSheet.create({
    container: {
      flexDirection: 'row',
      alignItems: 'center',
      width: '100%',
      paddingVertical: utils.spacing.md,
      ...(isMobile && {
        flexWrap: 'wrap',
        gap: utils.spacing.sm,
      }),
    },
    phaseContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.sm,
      flex: isMobile ? (isSmallMobile ? 1 : 0) : 1,
      minWidth: isMobile ? (isSmallMobile ? '48%' : 120) : 0,
      ...Platform.select({
        web: {
          cursor: 'default',
        } as any,
      }),
    },
    phaseClickable: {
      ...Platform.select({
        web: {
          cursor: 'pointer',
        } as any,
      }),
    },
    phaseCircle: {
      width: isMobile ? 32 : 40,
      height: isMobile ? 32 : 40,
      borderRadius: utils.radius.full,
      backgroundColor: addColorOpacity(tokens.color.onSurface, 0.1),
      borderWidth: 2,
      borderColor: tokens.color.border,
      alignItems: 'center',
      justifyContent: 'center',
    },
    phaseCircleActive: {
      backgroundColor: tokens.color.primary,
      borderColor: tokens.color.primary,
    },
    phaseCircleCompleted: {
      backgroundColor: tokens.color.success,
      borderColor: tokens.color.success,
    },
    phaseCircleUpcoming: {
      backgroundColor: tokens.color.surface,
      borderColor: tokens.color.border,
    },
    phaseNumber: {
      fontSize: isMobile ? utils.typography.sizes.sm : utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    phaseNumberActive: {
      color: tokens.color.onPrimary,
    },
    phaseContent: {
      flex: 1,
      gap: 2,
    },
    phaseLabel: {
      fontSize: isSmallMobile ? utils.typography.sizes.xs : utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.medium,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
    },
    phaseLabelActive: {
      color: tokens.color.primary,
      fontWeight: utils.typography.weights.semibold,
    },
    phaseLabelCompleted: {
      color: tokens.color.success,
    },
    phaseLabelUpcoming: {
      color: addColorOpacity(tokens.color.onSurface, 0.4),
    },
    phaseDescription: {
      fontSize: utils.typography.sizes.xs,
      color: addColorOpacity(tokens.color.onSurface, 0.5),
      fontFamily: utils.typography.body.fontFamily,
    },
    connector: {
      flex: isMobile ? 0 : 1,
      height: 2,
      backgroundColor: tokens.color.border,
      marginHorizontal: isMobile ? utils.spacing.xs : utils.spacing.md,
      minWidth: isMobile ? 8 : 0,
    },
    connectorCompleted: {
      backgroundColor: tokens.color.success,
    },
    connectorUpcoming: {
      backgroundColor: tokens.color.border,
    },
  });
}

